import React, { useMemo, useRef } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';

// Full-screen procedural nebula + dust pass.
// Draws a stable, non-clipping background with light colored blobs and tiny dust speckles.
const Nebulae: React.FC = () => {
  // We removed the full-screen shader quad: nebula clouds are now provided by
  // the soft cloud billboards and the small 3D dust points below.

  // Add several 3D star/dust layers to create depth. Each Points object follows the camera with a parallax factor.
  const pointsLayers = useMemo(() => {
    // Use larger spherical shells so the shader quad provides the soft clouds
    // and the points act as fine star-dust (small, faint). Do not follow the camera
    // every frame — keep them in world space to avoid getting points extremely
    // close to the camera which makes them appear huge.
    // Use radii that stay inside a typical camera far plane (e.g. 1000)
    // to avoid objects being clipped out of view. These radii give depth
    // but remain visible when the camera rotates.
    // Make dust layers subtler: fewer points, smaller sizes and lower opacity.
    const layersConfig = [
      { count: 50, radius: 200, size: 0.6, parallax: 0.98 },
      { count: 150, radius: 400, size: 0.45, parallax: 0.94 },
      { count: 400, radius: 700, size: 0.3, parallax: 0.88 },
      { count: 500, radius: 1000, size: 0.25, parallax: 0.82 },
    ];

    const created: THREE.Points[] = [];

    for (let li = 0; li < layersConfig.length; li++) {
      const cfg = layersConfig[li];
      const positions = new Float32Array(cfg.count * 3);
      const colors = new Float32Array(cfg.count * 3);

      for (let i = 0; i < cfg.count; i++) {
        // random point on a sphere shell with jitter
        const u = Math.random();
        const v = Math.random();
        const theta = 2.0 * Math.PI * u;
        const phi = Math.acos(2.0 * v - 1.0);
        const r = cfg.radius * (0.85 + Math.random() * 0.3);
        const x = r * Math.sin(phi) * Math.cos(theta);
        const y = r * Math.sin(phi) * Math.sin(theta);
        const z = r * Math.cos(phi);
        positions[i * 3 + 0] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;

  // color sampled from the nebula palette with more variety (purple, blue, red, green, orange)
  const t = Math.random();
  const colorChoice = Math.floor(Math.random() * 5);
  let ca, cb;
  if (colorChoice === 0) {
    // Purple to magenta
    ca = new THREE.Color(0.72, 0.48, 0.92).lerp(new THREE.Color(0.5,0.5,0.5), 0.25);
    cb = new THREE.Color(0.92, 0.48, 0.82).lerp(new THREE.Color(0.5,0.5,0.5), 0.25);
  } else if (colorChoice === 1) {
    // Blue to cyan
    ca = new THREE.Color(0.44, 0.78, 0.92).lerp(new THREE.Color(0.5,0.5,0.5), 0.25);
    cb = new THREE.Color(0.48, 0.88, 0.88).lerp(new THREE.Color(0.5,0.5,0.5), 0.25);
  } else if (colorChoice === 2) {
    // Red to orange
    ca = new THREE.Color(0.92, 0.42, 0.48).lerp(new THREE.Color(0.5,0.5,0.5), 0.3);
    cb = new THREE.Color(0.92, 0.65, 0.42).lerp(new THREE.Color(0.5,0.5,0.5), 0.3);
  } else if (colorChoice === 3) {
    // Green to teal
    ca = new THREE.Color(0.48, 0.85, 0.58).lerp(new THREE.Color(0.5,0.5,0.5), 0.3);
    cb = new THREE.Color(0.42, 0.82, 0.75).lerp(new THREE.Color(0.5,0.5,0.5), 0.3);
  } else {
    // Pink to light purple
    ca = new THREE.Color(0.88, 0.55, 0.75).lerp(new THREE.Color(0.5,0.5,0.5), 0.3);
    cb = new THREE.Color(0.78, 0.58, 0.88).lerp(new THREE.Color(0.5,0.5,0.5), 0.3);
  }
  const c = ca.clone().lerp(cb, t).multiplyScalar(0.7 + Math.random() * 0.15);
        colors[i * 3 + 0] = c.r;
        colors[i * 3 + 1] = c.g;
        colors[i * 3 + 2] = c.b;
      }

      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      const mat = new THREE.PointsMaterial({
        size: cfg.size,
        vertexColors: true,
        transparent: true,
        // reduce dust visibility further
        opacity: 0.015,
        depthTest: true,
        depthWrite: false,
        sizeAttenuation: true,
        blending: THREE.NormalBlending,
      });

      const pts = new THREE.Points(geom, mat);
      pts.frustumCulled = false;
      // store parallax factor on the object for runtime positioning
      // @ts-ignore attach custom
      pts.userData.parallax = cfg.parallax;
      created.push(pts);
    }

    return created;
  }, []);

  // Cloud sprite layers (soft nebula-like billboards)
  const cloudTexture = useLoader(THREE.TextureLoader, 'https://assets.codepen.io/682745/cloud2.png');
  const cloudLayers = useMemo(() => {
    // Cloud billboard layers kept within viewable range, but tuned to look like
    // nebula volumes: subtle color tint, per-mesh opacity/scale variation.
    // Make cloud billboards much less visible: fewer sprites and lower base opacity
    // revert to single-plane billboards per cloud (previous simpler style)
    const layers = [
      { count: 6, radius: 300, size: 220, opacity: 0.008 },
      { count: 15, radius: 500, size: 160, opacity: 0.005 },
      { count: 30, radius: 800, size: 110, opacity: 0.004 },
      { count: 40, radius: 1200, size: 90, opacity: 0.003 },
    ];

  const created: THREE.Object3D[] = [];

    for (let li = 0; li < layers.length; li++) {
      const cfg = layers[li];
      for (let i = 0; i < cfg.count; i++) {
        const planeGeom = new THREE.PlaneGeometry(cfg.size, cfg.size);
        // tint the cloud with varied nebula colors (purple, blue, red, green, pink)
        const t = Math.random();
        const colorChoice = Math.floor(Math.random() * 5);
        let baseA, baseB;
        if (colorChoice === 0) {
          // Purple to magenta
          baseA = new THREE.Color(0.72, 0.48, 0.92).lerp(new THREE.Color(0.55,0.55,0.55), 0.35);
          baseB = new THREE.Color(0.92, 0.48, 0.82).lerp(new THREE.Color(0.55,0.55,0.55), 0.35);
        } else if (colorChoice === 1) {
          // Blue to cyan
          baseA = new THREE.Color(0.44, 0.78, 0.92).lerp(new THREE.Color(0.55,0.55,0.55), 0.35);
          baseB = new THREE.Color(0.48, 0.88, 0.88).lerp(new THREE.Color(0.55,0.55,0.55), 0.35);
        } else if (colorChoice === 2) {
          // Red to orange
          baseA = new THREE.Color(0.92, 0.42, 0.48).lerp(new THREE.Color(0.55,0.55,0.55), 0.4);
          baseB = new THREE.Color(0.92, 0.65, 0.42).lerp(new THREE.Color(0.55,0.55,0.55), 0.4);
        } else if (colorChoice === 3) {
          // Green to teal
          baseA = new THREE.Color(0.48, 0.85, 0.58).lerp(new THREE.Color(0.55,0.55,0.55), 0.4);
          baseB = new THREE.Color(0.42, 0.82, 0.75).lerp(new THREE.Color(0.55,0.55,0.55), 0.4);
        } else {
          // Pink to light purple
          baseA = new THREE.Color(0.88, 0.55, 0.75).lerp(new THREE.Color(0.55,0.55,0.55), 0.4);
          baseB = new THREE.Color(0.78, 0.58, 0.88).lerp(new THREE.Color(0.55,0.55,0.55), 0.4);
        }
        const tint = baseA.clone().lerp(baseB, t);

        // Create a small crossed-planes group so the cloud has visible
        // thickness from any viewing angle (avoids thin-line edge when seen
        // almost edge-on). We keep two planes crossed at 90° for low cost.
        const group = new THREE.Group();
        const perPlaneOpacity = cfg.opacity * (0.35 + Math.random() * 0.3);
        const mat = new THREE.MeshBasicMaterial({
          map: cloudTexture,
          color: tint,
          transparent: true,
          opacity: perPlaneOpacity,
          depthTest: true,
          depthWrite: false,
          blending: THREE.NormalBlending,
          side: THREE.DoubleSide,
          toneMapped: false,
        });

        // Two crossed planes (0 and 90°) to give volume from all directions
        for (let p = 0; p < 2; p++) {
          const plane = new THREE.Mesh(planeGeom, mat);
          // rotate the second plane by 90° around Y
          if (p === 1) plane.rotation.y = Math.PI / 2;
          // small random rotation so not perfectly symmetric
          plane.rotation.z += (Math.random() - 0.5) * 0.6;
          plane.rotation.x += (Math.random() - 0.5) * 0.25;
          // slight offset along local normal to create a tiny thickness
          plane.translateZ((p === 0 ? 1 : -1) * (cfg.size * 0.02));
          // per-plane drift speed (small)
          // @ts-ignore
          plane.userData.rotSpeed = 0.00002 + Math.random() * 0.00015;
          group.add(plane);
        }
  // place on a spherical shell uniformly over the full sphere
  // Use z-uniform sampling to avoid any polar/equator bias
  const r = cfg.radius * (0.9 + Math.random() * 0.2);
  const zUnit = 2.0 * Math.random() - 1.0; // in [-1,1]
  const theta = 2.0 * Math.PI * Math.random();
  const rxy = Math.sqrt(Math.max(0, 1 - zUnit * zUnit));
  let x = r * rxy * Math.cos(theta);
  let y = r * rxy * Math.sin(theta);
  let z = r * zUnit;
  // add a small random jitter so sprites don't lie exactly on a shell
  const jitterScale = Math.max(1, cfg.size * 0.08);
  x += (Math.random() - 0.5) * jitterScale;
  y += (Math.random() - 0.5) * jitterScale;
  z += (Math.random() - 0.5) * jitterScale;
        // place group and add small random group rotation so each cloud reads
        // slightly different in the sky
        group.position.set(x, y, z);
        group.rotation.x = Math.random() * Math.PI;
        group.rotation.y = Math.random() * Math.PI * 2;
        group.rotation.z = Math.random() * Math.PI;

        // Store data for animation on the group (will be applied to child planes)
        // @ts-ignore
        group.userData.rotSpeed = 0.00003 + Math.random() * 0.0002;
        // @ts-ignore
        group.userData.baseOpacity = perPlaneOpacity;

        // Scale variation
        const sJitter = 0.9 + Math.random() * 0.25;
        group.scale.set(sJitter, sJitter, sJitter);
        group.frustumCulled = false;
        created.push(group);
      }
    }

    return created;
  }, [cloudTexture]);

  // (glow points removed) - nebula highlights are provided by billboards and subtle dust

  // Update shader time and position of points layers for parallax each frame
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      // Do NOT reposition the group to the camera; keep shells in world-space.
      // Add a very slow rotation to layers so the dust drifts subtly.
      groupRef.current.children.forEach((child, idx) => {
        // rotate cloud billboards subtly; points will follow group rotation
        if ((child as any).isPoints) {
          // no per-layer rotation: points are static in this implementation
        }

        // Animate crossed-plane cloud groups (preferred) or single-plane meshes
        if ((child as any).isGroup) {
          const g = child as THREE.Group;
          // @ts-ignore
          const rotSpeed = g.userData.rotSpeed ?? 0.0001;
          // slight group drift
          g.rotation.z += rotSpeed * state.clock.getDelta();
          g.children.forEach((planeMesh) => {
            if ((planeMesh as any).isMesh && (planeMesh as THREE.Mesh).material) {
              // apply tiny z-rotation drift per plane
              // @ts-ignore
              planeMesh.rotation.z += (planeMesh.userData?.rotSpeed ?? rotSpeed * 0.7) * state.clock.getDelta();
              // animate opacity (breathing)
              // @ts-ignore
              const base = g.userData.baseOpacity ?? (planeMesh.material as any).opacity ?? 0.02;
              // @ts-ignore
              (planeMesh.material as THREE.MeshBasicMaterial).opacity = base * (0.85 + 0.15 * Math.sin(state.clock.elapsedTime * (0.05 + rotSpeed * 10)));
            }
          });
        } else if ((child as any).isMesh && (child as THREE.Mesh).geometry.type === 'PlaneGeometry') {
          // fallback single-plane behavior (shouldn't be common now)
          const m = child as THREE.Mesh;
          // apply tiny z-rotation drift
          // @ts-ignore
          m.rotation.z += (m.userData.rotSpeed ?? 0.001) * state.clock.getDelta();
          // gently modulate opacity for breathing nebula feel
          // @ts-ignore
          if (m.material) {
            // @ts-ignore
            const base = m.userData.baseOpacity ?? (m.material as any).opacity ?? 0.02;
            // @ts-ignore
            (m.material as THREE.MeshBasicMaterial).opacity = base * (0.85 + 0.15 * Math.sin(state.clock.elapsedTime * (0.05 + (m.userData.rotSpeed || 0) * 10)));
          }
        }
      });
      // Previously we rotated and followed the camera target here. Revert
      // to fixed world-space nebula positioning and no automatic group
      // rotation so the background stays subtle and stable relative to the
      // rest of the scene.
    }
  });

  return (
    <group ref={groupRef} renderOrder={-1000}>
      {/* 3D dust/star layers */}
      {pointsLayers.map((p, i) => (
        <primitive key={`points-${i}`} object={p} />
      ))}

      {/* soft cloud billboards (nebula volumes) */}
      {cloudLayers.map((c, i) => (
        <primitive key={`cloud-${i}`} object={c} />
      ))}
    </group>
  );
};

export default Nebulae;
